/**
 * DraftRecoveryNotice — offers back work that an idle sign-out interrupted.
 *
 * When the idle warning fires, Designer writes the survey to local storage.
 * This is what the author meets the next time they open the app: a plain offer
 * to take that work back, or to throw it away.
 *
 * ── Why it asks instead of just restoring ──
 *
 * Somebody who deliberately abandoned a bad edit would otherwise be handed it
 * straight back, with no idea where the change came from. That is its own kind
 * of data loss, and it is worse because it is invisible. So the answer is
 * always the author's.
 *
 * The notice does not block the canvas. An author who wants to ignore it and
 * carry on can, and the draft stays where it is until they decide.
 */

import React from 'react';

export default function DraftRecoveryNotice({ draft, onRestore, onDiscard }) {
    if (!draft) return null;

    const when = formatSavedAt(draft.savedAt);
    const what = draft.label ? `“${draft.label}”` : 'A survey';

    return (
        <div className="draft-recovery" role="status">
            <div className="draft-recovery__text">
                <strong className="draft-recovery__title">Work from your last session was not saved.</strong>
                <span className="draft-recovery__body">
                    {what} was kept in this browser when you were signed out{when ? ` at ${when}` : ''}.
                </span>
            </div>
            <div className="draft-recovery__actions">
                <button type="button" className="draft-recovery__restore" onClick={onRestore}>
                    Restore it
                </button>
                <button type="button" className="draft-recovery__discard" onClick={onDiscard}>
                    Discard it
                </button>
            </div>
        </div>
    );
}

/** "14:32" today, or "17 Aug, 14:32" otherwise. Empty when we cannot tell. */
export function formatSavedAt(savedAt, now = new Date()) {
    if (!savedAt || typeof savedAt !== 'number') return '';
    const at = new Date(savedAt);
    if (Number.isNaN(at.getTime())) return '';
    const time = `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
    const sameDay = at.getFullYear() === now.getFullYear()
        && at.getMonth() === now.getMonth()
        && at.getDate() === now.getDate();
    if (sameDay) return time;
    const month = at.toLocaleString(undefined, { month: 'short' });
    return `${at.getDate()} ${month}, ${time}`;
}
